import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Transaction } from '../../../../../Overview/types';
import {
  Band,
  IndicatorSingleValue,
  ProfitResultIndicatorConfigs,
  Transaction as ProfitTransaction,
  ZoningBand,
} from '../../../../../ProfitCalculations/types';
import './index.css';
import { colors } from '../../../../../../colors';
import {
  DomainTuple,
  Point,
  VictoryAxis,
  VictoryCandlestick,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
} from 'victory';
import moment from 'moment';
import {
  formatBalance,
  formatDate,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '../../../../../../helpers/NumberFormatter';
import {
  CbrtRangeState,
  CompactTransaction,
  MrcState,
  SingleProfitCalculationApiResponse,
  WakeupTimes,
} from '../../../../../ProfitCalculations/types';
import { BiExitFullscreen } from 'react-icons/bi';
import ChartDensityOptions from './ChartDensityOptions';
import {
  createXTickValues,
  getFormatUnit,
} from '../../../../../../helpers/bundleTransactionsToCompactForm';
import { calculateStartOfWaveTimestamp } from '../../../../../../helpers/calculateStartOfWaveTimestamp';
import { formatParam } from '../../../../../Control/Content/helpers';
import axios from 'axios';
import { darkenColor } from '../../../../../../helpers/colors';
import { getTimeFrameIntervalTimestamp } from '../../../../Charts/PriceChart/helpers';
import { Bot, BotTimeFrames, Candle, Scale } from '../../../../types';
import LoadingIcons from 'react-loading-icons';
import MultiChart, { XAxisInfo } from '../../../../../../components/MultiChart';
import IndicatorWrapper from '../../../../../../components/Charts/IndicatorWrapper';
import { ComponentDimensions } from '../../../../../../types/Layout';
import BandChart from '../../../../../../components/Charts/CandleSticks/BandChart';
import { isZoningBand } from '../../../../../../components/Charts/CandleSticks';
import {
  DataPoint,
  createYAxisCandlesticksTicks,
  formatToCandlestickDataPointType,
  formatYTick,
} from '../../../../../../helpers/Chart';

type MovingAverage = {
  x: number;
  y: number;
};

import ServicePaths from '../../../../../../bridge/utils/ServicePaths';
import { TimeFrame } from '../../../../../../types/Bot';

export type CandleInfo = {
  high: number;
  close: number;
  open: number;
  low: number;
  startTimestamp: number;
  endTimestamp: number;
  direction: string;
};

export type MrcStateDataPoint = {
  x: number;
  y: number;
  color: string;
  date?: Date;
  label?: string;
  orderType?: string;
};

interface Props {
  bot: Bot | undefined;
  isCompact: boolean;
  transaction: Transaction;
  compactTransactions: CompactTransaction[];
  handleCloseChartView: () => void;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  timeFrames?: BotTimeFrames | undefined;
  wakeupTimes?: WakeupTimes;
  clusteredTransactions: Transaction[];
  indicatorConfig?: ProfitResultIndicatorConfigs;
}

interface ChartProps {
  timeFrame: TimeFrame;
  transaction: Transaction;
  higherTCandles: Candle[] | undefined;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  density: number;
  isCompare: boolean;
  bot: Bot | undefined;
  realWakeUpTimes: number[] | undefined;
  profitCalcsWakeUpTimes: WakeupTimes | undefined;
  isCompact: boolean;
  compactTransactions: CompactTransaction[];
  clusteredTransactions: Transaction[];
  profitCalcsTransactions: ProfitTransaction[];
  hasTimeFrameDifference: boolean;
  temporaryCandles: Candle[] | undefined;
  transactionCandles: Candle[] | undefined;
  setCandleInfo: React.Dispatch<React.SetStateAction<CandleInfo | undefined>>;

  height?: number | undefined;
  width?: number | undefined;
  setXAxisInfo?: React.Dispatch<React.SetStateAction<XAxisInfo | undefined>>;
  movingAverages?: Array<{
    data: IndicatorSingleValue[];
    color: string;
    label: string;
  }>;
  bollingerBand?: {
    data: Band | ZoningBand | undefined;
    signalPeriod: number | null;
  };
  keltnerChannel?: {
    data: Band | ZoningBand | undefined;
    signalPeriod: number | null;
  };
}

const Chart = (props: ChartProps) => {
  const {
    transaction,
    cbrtRangeStates,
    mrcStates,
    density,
    timeFrame,
    isCompare,
    isCompact,
    higherTCandles,
    bot,
    realWakeUpTimes,
    profitCalcsWakeUpTimes,
    compactTransactions,
    clusteredTransactions,
    profitCalcsTransactions,
    hasTimeFrameDifference,
    temporaryCandles,
    transactionCandles,
    setCandleInfo,
  } = props;

  const {
    height,
    width,
    setXAxisInfo,
    movingAverages,
    bollingerBand,
    keltnerChannel,
  } = props;

  const candleDataPoints: DataPoint[] = useMemo(() => {
    const data = hasTimeFrameDifference ? temporaryCandles : transactionCandles;

    if (!data?.length) {
      return [];
    }

    return data.map((d) => formatToCandlestickDataPointType(d));
  }, [hasTimeFrameDifference, temporaryCandles, transactionCandles]);

  const tickValues = useMemo(() => {
    if (!candleDataPoints.length) {
      return [];
    }

    return createXTickValues(
      candleDataPoints[0].x,
      candleDataPoints[candleDataPoints.length - 1].x,
    );
  }, [candleDataPoints]);

  const findClosestCandleTimestamp = useCallback(
    (timestamp: number) => {
      if (!candleDataPoints) {
        return timestamp;
      }

      return (
        candleDataPoints.find((c) => c.x <= timestamp && timestamp <= c.x2)
          ?.x || 0
      );
    },
    [candleDataPoints],
  );

  const getTransactionTotalValue = useCallback(
    (transaction: any /*Transaction | CustomTransaction*/) => {
      return (
        transaction.currentStableBalance +
        transaction.currentAssetBalance *
          (transaction.orderPrice ?? transaction.currentPrice)
      );
    },
    [],
  );

  const getTransactionTotalAssetValue = useCallback(
    (transaction: any /*Transaction | CustomTransaction*/) => {
      return (
        transaction.currentStableBalance /
          (transaction.orderPrice ?? transaction.currentPrice) +
        transaction.currentAssetBalance
      );
    },
    [],
  );

  const generateTooltipLabel = useCallback(
    (
      transaction: Transaction | CompactTransaction,
      profitValue: number | null,
    ) => {
      const formattedDate = formatDate(transaction.transactionTimestamp || 0);

      const isCustomTransaction = transaction.hasOwnProperty('isDistribution');

      if (isCustomTransaction) {
        const transactionType = transaction.transactionType;
        const isStableBalance = transactionType.indexOf('STABLE') !== -1;
        const stableAmount = formatBalance(transaction.stableQuantity, true);
        const assetAmount = `${formatBalance(
          transaction.assetQuantity,
          false,
        )}`;

        const amount = isStableBalance
          ? `${stableAmount} (${assetAmount})`
          : `${assetAmount} (${stableAmount})`;

        return `${formattedDate}\n${transactionType}\n${amount}`;
      }

      const isBuy = transaction.orderType === 'BUY';

      const stableBalance = formatBalance(
        getTransactionTotalValue(transaction),
      );
      const assetBalance = `${formatBalance(
        getTransactionTotalAssetValue(transaction),
        false,
      )}`;
      const balance = isBuy ? assetBalance : stableBalance;

      const profit = formatPercentage(profitValue || 0);
      const price = formatPrice(
        transaction.averageOrderPrice || transaction.orderPrice,
        true,
        6,
      );

      return `${formattedDate}\n${balance} | ${profit}\n${price}`;
    },
    [getTransactionTotalAssetValue, getTransactionTotalValue],
  );

  const transactionDataPoints = useMemo(() => {
    if (!isCompact) {
      return [
        {
          x: findClosestCandleTimestamp(transaction.transactionTimestamp || 0),
          y: transaction.averageOrderPrice || transaction.orderPrice,
          color: transaction.orderType === 'BUY' ? colors.green : colors.red,
          label: generateTooltipLabel(transaction, transaction.profit),
          strokeColor: transaction.transactionType.includes('TAKE_PROFIT')
            ? colors.blue
            : colors.white,
        },
      ];
    }

    const index = compactTransactions.findIndex(
      (t) =>
        t.compactTransactionId?.toString() === transaction.compactTransactionId,
    );
    const updatedCompactTransactions = [...compactTransactions];

    if (density === 3) {
      updatedCompactTransactions.splice(
        index,
        1,
        ...(clusteredTransactions as unknown as CompactTransaction[]),
      );
    }

    const dataPoints = [];

    // if (isCompare && profitCalcsTransactions.length) {
    //   if (density !== 3 && bot) {
    //     const profitCompactTransactions = bundleTransactionsToCompactForm(
    //       profitCalcsTransactions,
    //     );

    //     dataPoints.push(
    //       ...formatTransactionType(profitCompactTransactions, bot).map((t) => {
    //         return {
    //           x: findClosestCandleTimestamp(t.transactionTimestamp || 0),
    //           y: t.averageOrderPrice || t.orderPrice,
    //           color: colors.blue,
    //           label: generateTooltipLabel(
    //             t,
    //             (t as unknown as Transaction).profit,
    //           ),
    //           strokeColor: t.transactionType.includes('TAKE_PROFIT')
    //             ? colors.blue
    //             : colors.white,
    //         };
    //       }),
    //     );
    //   } else
    if (bot) {
      dataPoints.push(
        ...profitCalcsTransactions.map((t) => {
          return {
            x: findClosestCandleTimestamp(t.transactionTimestamp),
            y: t.orderPrice,
            color: colors.blue,
            label: generateTooltipLabel(
              t,
              (t as unknown as Transaction).profit,
            ),
            strokeColor: t.transactionType.includes('TAKE_PROFIT')
              ? colors.blue
              : colors.white,
          };
        }),
      );
    }

    dataPoints.push(
      ...updatedCompactTransactions.map((t) => {
        return {
          x: findClosestCandleTimestamp(
            t.timestamp || t.transactionTimestamp || 0,
          ),
          y: t.averageOrderPrice || t.orderPrice,
          color: (t.orderType === 'BUY' ? colors.green : colors.red) as string,
          label: generateTooltipLabel(t, (t as unknown as Transaction).profit),
          strokeColor: t.transactionType.includes('TAKE_PROFIT')
            ? colors.blue
            : colors.white,
        };
      }),
    );

    return dataPoints;
  }, [
    bot,
    clusteredTransactions,
    compactTransactions,
    density,
    findClosestCandleTimestamp,
    generateTooltipLabel,
    isCompact,
    profitCalcsTransactions,
    transaction,
  ]);

  const signalDataPoints = useMemo(() => {
    return transaction.signals?.map((s) => {
      const formattedDate = formatDate(s.timestamp);
      const label = `${formattedDate}\nValue: ${s.value}\nPrice: ${formatPrice(
        s.price,
        true,
        2,
      )}`;

      return {
        x: findClosestCandleTimestamp(s.timestamp),
        y: s.price,
        color: (s.signalType === 'BUY' ? colors.green : colors.red) as string,
        label: label,
      };
    });
  }, [findClosestCandleTimestamp, transaction.signals]);

  const getCandleColor = useCallback(
    (c: any) => {
      if (density === 1) {
        return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
      }

      if (density === 3 && !!bot && !isCompare) {
        return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
      }

      let data;

      if (realWakeUpTimes) {
        data = [...realWakeUpTimes];
      }

      if (profitCalcsWakeUpTimes) {
        if (density === 2) {
          !!data
            ? data.push(...profitCalcsWakeUpTimes.shorterT)
            : [...profitCalcsWakeUpTimes.shorterT];
        } else if (density === 3) {
          !!data
            ? data.push(...profitCalcsWakeUpTimes.multiT)
            : [...profitCalcsWakeUpTimes.multiT];
        }
      }

      if (!data) {
        return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
      }

      // We basically want to do a CROSS JOIN between the real and profitCalcs wakeup times
      // We want to find all the wakeup times that only exists in 1 of the two arrays
      // We want this format: { wakeupTime: amount }
      const amountByWakeupTime = data.reduce<Record<string, number>>(
        (acc, curr) => {
          if (!acc[curr]) {
            acc[curr] = 1;
            return acc;
          }

          acc[curr] = acc[curr] + 1;
          return acc;
        },
        {},
      );

      if (density !== 1) {
        if (isCompare) {
          const amount = amountByWakeupTime[c.datum.startTimestamp];

          if (amount === 1) {
            return colors.white;
          }

          return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
        }

        if (data.includes(c.datum.startTimestamp)) {
          return c.datum.close >= c.datum.open ? '#008000' : '#FF0000';
        }
      }

      return darkenColor(
        c.datum.close >= c.datum.open ? '#008000' : '#FF0000',
        30,
      );
    },

    [density, bot, isCompare, realWakeUpTimes, profitCalcsWakeUpTimes],
  );

  const calculateTickValue = useCallback(
    (t: Date) => {
      if (!candleDataPoints?.length) {
        return moment(t).format('DD MMM');
      }

      const formatUnit = getFormatUnit(
        candleDataPoints[0].x,
        candleDataPoints[candleDataPoints.length - 1].x,
      );

      return moment(t).format(formatUnit);
    },
    [candleDataPoints],
  );

  const axisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: setXAxisInfo ? {} : { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 14, padding: 5, fill: 'gray' },
    }),
    [setXAxisInfo],
  );

  const yAxisStyle = useMemo(
    () => ({
      axis: { stroke: 'rgba(64,64,64,0.7)' },
      grid: {
        stroke: 'rgba(64,64,64,0.7)',
      },
      ticks: { stroke: 'white', size: 2 },
      tickLabels: { fontSize: 20, padding: 5, fill: '#999' },
    }),
    [],
  );

  const cbrtLines = useMemo(() => {
    if (!cbrtRangeStates?.length || !candleDataPoints?.length) {
      return [];
    }

    const filteredCbrtRangeStates: Array<
      {
        x: number;
        y: number;
        color: string;
        label?: string;
      }[]
    > = [];

    const visibleCbrtRangeStates = cbrtRangeStates.filter(
      (s) =>
        (candleDataPoints[0].x <= s.timestamp ||
          s.timestamp <= transaction.transactionTimestamp) &&
        s.timestamp <= transaction.transactionTimestamp &&
        (s.endTimestamp || new Date().valueOf()) > candleDataPoints[0].x &&
        transaction.orderType === (s.direction === 'up' ? 'BUY' : 'SELL'),
    );

    visibleCbrtRangeStates.forEach((cbrtRangeState, index) => {
      const isLast = index === visibleCbrtRangeStates.length - 1;

      const color =
        cbrtRangeState.direction === 'up' ? colors.green : colors.red;
      const close =
        cbrtRangeState.open +
        cbrtRangeState.cbrt *
          cbrtRangeState.open *
          (cbrtRangeState.direction === 'up' ? 1 : -1);

      const negativeClose = cbrtRangeState.open - (close - cbrtRangeState.open);

      /** Break positive side */
      filteredCbrtRangeStates.push([
        {
          x: cbrtRangeState.timestamp,
          y: close,
          color: isLast ? color : darkenColor(color, 20),
          label: `cbrt: ${formatNumber(close, 4)}`,
        },
        {
          x: cbrtRangeState.endTimestamp || new Date().valueOf(),
          y: close,
          color: isLast ? color : darkenColor(color, 20),
        },
      ]);

      const negativeColor =
        cbrtRangeState.direction === 'up'
          ? `${colors.red}66`
          : `${colors.green}66`;

      /** Break negative side */
      filteredCbrtRangeStates.push([
        {
          x: cbrtRangeState.timestamp,
          y: negativeClose,
          color: negativeColor,
          label: `cbrt: ${formatNumber(negativeClose, 4)}`,
        },
        {
          x: cbrtRangeState.endTimestamp || new Date().valueOf(),
          y: negativeClose,
          color: negativeColor,
        },
      ]);
    });

    return filteredCbrtRangeStates;
  }, [
    candleDataPoints,
    cbrtRangeStates,
    transaction.orderType,
    transaction.transactionTimestamp,
  ]);

  const mrcStatesLines = useMemo(() => {
    if (!mrcStates?.length || !candleDataPoints?.length || !timeFrame) {
      return [];
    }

    /** For each item, make a line with a start and end */
    const items: Array<Array<MrcStateDataPoint>> = [];

    const visibleMrcStates = mrcStates.filter(
      (s) =>
        candleDataPoints[0].x <= (s.endTimestamp || new Date().valueOf()) &&
        (s.endTimestamp || new Date().valueOf()) <=
          candleDataPoints[candleDataPoints.length - 1].x2 &&
        transaction.orderType === s.orderType,
    );

    visibleMrcStates.forEach((mrcState) => {
      if (mrcState.startTimestamp === mrcState.endTimestamp) {
        return;
      }

      if (mrcState.orderType !== transaction.orderType) {
        return;
      }

      const baseColor =
        mrcState.orderType === 'BUY' ? colors.green : colors.red;
      const color =
        mrcState.state === 'INITIALISED'
          ? darkenColor(baseColor, 10)
          : mrcState.state === 'PENDING'
          ? darkenColor(baseColor, 50)
          : baseColor;

      const start = mrcState.startTimestamp;
      const end = mrcState.endTimestamp || new Date().valueOf();

      if (
        start <= candleDataPoints[0].x ||
        end > candleDataPoints[candleDataPoints.length - 1].x
      ) {
        return;
      }

      /** If the state is longer than 1 candle */
      const isLongEnough = !timeFrame
        ? true
        : end - start >= getTimeFrameIntervalTimestamp(timeFrame);

      if (!isLongEnough) {
        return;
      }

      items.push([
        {
          x: start,
          y: mrcState.startPrice,
          color,
          date: new Date(start),
          label: `mrc: ${mrcState.state}`,
          orderType: mrcState.orderType,
        },
        {
          x: end,
          y: mrcState.startPrice,
          color,
          date: new Date(end),
        },
      ]);
    });

    return items;
  }, [candleDataPoints, mrcStates, timeFrame, transaction.orderType]);

  const channelMin = useMemo(() => {
    const channel = bollingerBand?.data || keltnerChannel?.data;

    if (!channel) {
      return Infinity;
    }

    if (isZoningBand(channel)) {
      return Math.min(
        ...channel.buyBand
          .filter(
            (c) =>
              candleDataPoints[0].x <= c.timestamp &&
              c.timestamp <= candleDataPoints[candleDataPoints.length - 1].x,
          )
          .map((c) => c.value),
      );
    }

    return Math.min(
      ...channel.lowerBand
        .filter(
          (c) =>
            candleDataPoints[0].x <= c.timestamp &&
            c.timestamp <= candleDataPoints[candleDataPoints.length - 1].x,
        )
        .map((c) => c.value),
    );
  }, [bollingerBand?.data, candleDataPoints, keltnerChannel?.data]);

  const channelMax = useMemo(() => {
    const channel = bollingerBand?.data || keltnerChannel?.data;

    if (!channel) {
      return -Infinity;
    }

    if (isZoningBand(channel)) {
      return Math.max(
        ...channel.sellBand
          .filter(
            (c) =>
              candleDataPoints[0].x <= c.timestamp &&
              c.timestamp <= candleDataPoints[candleDataPoints.length - 1].x,
          )
          .map((c) => c.value),
      );
    }

    return Math.max(
      ...channel.upperBand
        .filter(
          (c) =>
            candleDataPoints[0].x <= c.timestamp &&
            c.timestamp <= candleDataPoints[candleDataPoints.length - 1].x,
        )
        .map((c) => c.value),
    );
  }, [bollingerBand?.data, candleDataPoints, keltnerChannel?.data]);

  const domain = useMemo(() => {
    if (!candleDataPoints?.length) {
      return undefined;
    }

    const minY = Math.min(
      ...candleDataPoints.map((c) => c.low),
      ...cbrtLines.map((cl) => Math.min(...cl.map((c) => c.y))),
      ...mrcStatesLines.map((ms) => Math.min(...ms.map((s) => s.y))),
      channelMin,
    );

    const maxY = Math.max(
      ...candleDataPoints.map((c) => c.high),
      ...cbrtLines.map((cl) => Math.min(...cl.map((c) => c.y))),
      ...mrcStatesLines.map((ms) => Math.max(...ms.map((s) => s.y))),
      channelMax,
    );

    return {
      x: [
        candleDataPoints[0].x,
        candleDataPoints[candleDataPoints.length - 1].x,
      ] as DomainTuple,
      y: [minY * 0.92, maxY * 1.05] as DomainTuple,
    };
  }, [candleDataPoints, cbrtLines, channelMax, channelMin, mrcStatesLines]);

  const startOfWaveLines = useMemo(() => {
    if (!higherTCandles?.length || !domain) {
      return [];
    }
    /** For each transaction datapoint, we want to calculate the timestamp of the start of the wave
     * If the transaction dataPoint was a sell, color is red, otherwise color is green.
     */

    return higherTCandles
      .filter((c) => c.startOfNewTrend)
      .map((c) => ({
        x: c.startTimestamp,
        y: domain.y[1] as number,
        y0: 0,
        color: `${c.direction === 'up' ? colors.green : colors.red}38`,
      }));
  }, [domain, higherTCandles]);

  const movingAveragesDataSets = useMemo(() => {
    if (!movingAverages?.length) {
      return [];
    }
    const dataSets: Array<{ data: MovingAverage[]; color: string }> = [];

    movingAverages.forEach((movingAverage) => {
      dataSets.push({
        data: movingAverage.data.map((ma) => ({
          x: ma.timestamp,
          y: ma.value,
        })),
        color: movingAverage.color,
      });
    });

    return dataSets;
  }, [movingAverages]);

  const handleHover = useCallback(
    async (
      event: React.SyntheticEvent<any, Event>,
      eventProps: { index: number; data: any; datum: any },
    ) => {
      setCandleInfo({
        high: eventProps.datum.high,
        close: eventProps.datum.close,
        open: eventProps.datum.open,
        low: eventProps.datum.low,
        startTimestamp: eventProps.datum.x,
        endTimestamp: eventProps.datum.x2,
        direction: eventProps.datum.direction,
      });
    },
    [setCandleInfo],
  );

  const domainPadding = useMemo(() => {
    /** This state sometimes only shows a few candles
     * The fewer the candles, the closer they must be together, to higher the domainPadding
     * Use a minimum of 30
     */

    const amountOfCandles = candleDataPoints.length;
    return { x: Math.max(30, 68 - amountOfCandles) };
  }, [candleDataPoints.length]);

  const yTickValues = useMemo(() => {
    if (!domain) {
      return [];
    }

    return createYAxisCandlesticksTicks(
      candleDataPoints,
      [],
      bollingerBand?.data || keltnerChannel?.data,
      domain,
    );
  }, [bollingerBand?.data, candleDataPoints, domain, keltnerChannel?.data]);

  const paddingRight = useMemo(() => {
    if (!yTickValues?.length) {
      return 0;
    }

    const longestTick = yTickValues
      .sort((a, b) => b.toString().length - a.toString().length)[0]
      .toString();

    const length = longestTick.length;
    const hasDecimal = longestTick.includes(',') || longestTick.includes('.');
    return 45 + length * 10 - (hasDecimal ? 5 : 0);
  }, [yTickValues]);

  const chartPadding = useMemo(
    () => ({
      left: 0,
      right: paddingRight,
      bottom: setXAxisInfo ? 0 : 30,
      top: 50,
    }),
    [setXAxisInfo, paddingRight],
  );

  useEffect(() => {
    if (!domain?.x || !tickValues?.length) {
      return;
    }

    setXAxisInfo?.({
      tickValues,
      axisStyle,
      tickFormat: calculateTickValue,
      domain: { x: domain.x as [number, number] },
      domainPadding,
      chartPadding,
    });
  }, [
    axisStyle,
    calculateTickValue,
    chartPadding,
    domain?.x,
    domainPadding,
    setXAxisInfo,
    tickValues,
  ]);

  const chartWidth = width;
  const chartHeight = height;

  return (
    <div
      className={'candlesticks-container'}
      style={{ width: chartWidth, height: chartHeight }}
    >
      <VictoryChart
        domainPadding={domainPadding}
        padding={chartPadding}
        domain={domain}
        width={chartWidth}
        height={chartHeight}
        scale={Scale.Logarithmic}
      >
        <VictoryAxis
          tickFormat={!setXAxisInfo ? calculateTickValue : () => ''}
          style={axisStyle}
          tickValues={tickValues}
          fixLabelOverlap
        />
        <VictoryAxis
          dependentAxis
          style={yAxisStyle}
          tickFormat={formatYTick}
          orientation={'right'}
          tickValues={yTickValues}
          tickLabelComponent={<VictoryLabel dx={5} />}
        />

        <BandChart band={bollingerBand?.data} domain={domain} />
        <BandChart band={keltnerChannel?.data} domain={domain} />

        {movingAveragesDataSets &&
          movingAveragesDataSets.map(({ data, color }, i) => (
            <VictoryLine
              key={`moving_average_${i}`}
              data={data}
              style={{
                data: {
                  stroke: color,
                  strokeWidth: 1,
                  opacity: 0.75,
                },
              }}
            />
          ))}

        {startOfWaveLines.map((wave, index) => (
          <VictoryLine
            key={`start_of_wave_line_${index}`}
            data={[{ ...wave, y: 0 }, wave]}
            style={{
              data: {
                stroke: wave.color,
                strokeWidth: 3,
              },
            }}
          />
        ))}

        <VictoryCandlestick
          data={candleDataPoints}
          style={{
            data: {
              stroke: getCandleColor,
              strokeWidth: 0.5,
              fill: getCandleColor,
            },
          }}
          events={[
            {
              target: 'data',
              eventHandlers: {
                onMouseOver: handleHover,
              },
            },
          ]}
        />

        {cbrtLines.map((line, index) => (
          <VictoryLine
            key={`cbrt_line_${index}`}
            data={line}
            padding={10}
            style={{
              data: {
                stroke: line[0].color,
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryLabel
                textAnchor={'start'}
                dy={-5}
                style={{
                  fill: line[0].color,
                  fontSize: 12,
                  fontWeight: 600,
                }}
                backgroundStyle={{ fill: 'rgba(0, 0, 0, 0.5)' }}
                backgroundPadding={{ top: 4, bottom: 0, left: 2, right: 6 }}
              />
            }
          />
        ))}

        {mrcStatesLines.map((line, index) => (
          <VictoryLine
            key={`mrc_line_${index}`}
            data={line}
            style={{
              data: {
                stroke: line[0].color,
                strokeWidth: 0.5,
              },
            }}
            labelComponent={
              <VictoryLabel
                textAnchor={'start'}
                dy={line[0].orderType === 'SELL' ? 9 : -3}
                dx={1}
                style={{
                  fill: line[0].color,
                  fontSize: 12,
                  fontWeight: 600,
                }}
                backgroundStyle={{ fill: 'rgba(0, 0, 0, 0.5)' }}
                backgroundPadding={{ top: 2, left: 2, right: 2 }}
              />
            }
          />
        ))}

        <VictoryScatter
          data={transactionDataPoints}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
              stroke: (d) => d.datum.strokeColor,
              strokeWidth: 0.75,
            },
          }}
          dataComponent={
            <Point className={'single-profit-calcs-chart-scatter-dot'} />
          }
          labelComponent={
            <VictoryTooltip
              constrainToVisibleArea
              cornerRadius={3}
              orientation={'left'}
              centerOffset={{ x: -10, y: -40 }}
              pointerOrientation="right"
              pointerWidth={20}
              flyoutStyle={{
                stroke: (d) => d.datum.color,
                strokeWidth: 1.5,
                fill: 'rgba(255, 255, 255, 0.8)',
              }}
              flyoutPadding={{ left: 20, right: 20, top: 5, bottom: 5 }}
              labelComponent={
                <VictoryLabel
                  textAnchor="start"
                  dx={(d) => (d.datum.isBuy === null ? -60 : -55)}
                  style={[
                    {
                      fill: 'rgb(0, 0, 0)',
                      fontFamily: 'monospace',
                      fontSize: 10,
                      fontWeight: 800,
                    },
                  ]}
                />
              }
            />
          }
          size={5}
        />

        {signalDataPoints && (
          <VictoryScatter
            data={signalDataPoints}
            style={{
              data: {
                fill: ({ datum }) => datum.color,
                stroke: colors.blue,
                strokeWidth: 0.75,
              },
            }}
            dataComponent={
              <Point className={'single-profit-calcs-chart-scatter-dot'} />
            }
            labelComponent={
              <VictoryTooltip
                constrainToVisibleArea
                cornerRadius={3}
                orientation={'left'}
                centerOffset={{ x: -10, y: -40 }}
                pointerOrientation="right"
                pointerWidth={20}
                flyoutStyle={{
                  stroke: (d) => d.datum.color,
                  strokeWidth: 1.5,
                  fill: 'rgba(255, 255, 255, 0.8)',
                }}
                flyoutPadding={{ left: 20, right: 20, top: 5, bottom: 5 }}
                labelComponent={
                  <VictoryLabel
                    textAnchor="start"
                    dx={(d) => (d.datum.isBuy === null ? -60 : -55)}
                    style={[
                      {
                        fill: 'rgb(0, 0, 0)',
                        fontFamily: 'monospace',
                        fontSize: 10,
                        fontWeight: 800,
                      },
                    ]}
                  />
                }
              />
            }
            size={3}
          />
        )}
      </VictoryChart>
    </div>
  );
};

const CandleView = (props: Props) => {
  const {
    bot,
    isCompact,
    transaction,
    compactTransactions,
    handleCloseChartView,
    timeFrames,
    clusteredTransactions,
    indicatorConfig,
  } = props;

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  const [density, setDensity] = useState(1);
  const [isCompare, setIsCompare] = useState(false);
  const [enableWakeupTimeColor, setEnableWakeupTimeColor] = useState(false);
  const [useParentIndicators, setUseParentIndicators] = useState(false);

  const [candleInfo, setCandleInfo] = useState<CandleInfo | undefined>(
    transaction.candles?.higher
      ? transaction.candles.higher[transaction.candles.higher.length - 1]
      : undefined,
  );

  const [cbrtRangeStates, setCbrtRangeStates] = useState<
    CbrtRangeState[] | undefined
  >(props.cbrtRangeStates);

  const [mrcStates, setMrcStates] = useState<MrcState[] | undefined>(
    props.mrcStates,
  );

  const [profitCalcsWakeUpTimes, setProfitCalcsWakeUpTimes] = useState(
    props.wakeupTimes,
  );

  const [profitCalcsTransactions, setProfitCalcsTransactions] = useState<
    ProfitTransaction[]
  >([]);

  const [realWakeUpTimes, setRealWakeUpTimes] = useState<
    number[] | undefined
  >();

  const [transactionHigherTCandles, setTransactionHigherTCandles] = useState<
    Candle[] | undefined
  >();

  const [temporaryCandles, setTemporaryCandles] = useState<
    Candle[] | undefined
  >();

  const [shouldFetchNewCandles, setShouldFetchNewCandles] =
    useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);

  const timeFrame = useMemo(() => {
    switch (density) {
      case 1:
        return transaction.timeFrames?.higher || timeFrames?.higher;

      case 2:
        return transaction.timeFrames?.shorter || timeFrames?.shorter;

      case 3:
        return transaction.timeFrames?.multiOrder || timeFrames?.multiOrder;

      default:
        return '' as TimeFrame;
    }
  }, [
    density,
    timeFrames?.higher,
    timeFrames?.multiOrder,
    timeFrames?.shorter,
    transaction.timeFrames?.higher,
    transaction.timeFrames?.multiOrder,
    transaction.timeFrames?.shorter,
  ]);

  const hasTimeFrameDifference = useMemo(
    () =>
      !!bot &&
      (bot?.timeFrame.higher !== transaction.timeFrames?.higher ||
        bot?.timeFrame.shorter !== transaction.timeFrames?.shorter ||
        bot?.timeFrame.multiOrder !== transaction.timeFrames?.multiOrder),
    [
      bot,
      transaction.timeFrames?.higher,
      transaction.timeFrames?.multiOrder,
      transaction.timeFrames?.shorter,
    ],
  );

  const transactionCandles = useMemo(() => {
    switch (density) {
      case 1:
        return transaction.candles?.higher;

      case 2:
        return transaction.candles?.shorter;

      case 3:
        const interval =
          (transaction.candles?.multi[1]?.endTimestamp ?? 0) -
          (transaction.candles?.multi[0]?.endTimestamp ?? 0);

        return transaction.candles?.multi.filter(
          (c) =>
            transaction.transactionTimestamp - interval * 10 <= c.endTimestamp,
        );

      default:
        return [];
    }
  }, [
    density,
    transaction.candles?.higher,
    transaction.candles?.multi,
    transaction.candles?.shorter,
    transaction.transactionTimestamp,
  ]);

  const higherTCandles = useMemo(
    () =>
      (transaction.timeFrames?.higher || timeFrames?.higher) !==
      bot?.timeFrame.higher
        ? transactionHigherTCandles || transaction.candles?.higher
        : transaction.candles?.higher,
    [
      bot?.timeFrame.higher,
      timeFrames?.higher,
      transaction.candles?.higher,
      transaction.timeFrames?.higher,
      transactionHigherTCandles,
    ],
  );

  const indicatorConfigByDensity = useMemo(() => {
    if (!indicatorConfig) {
      return undefined;
    }

    switch (density) {
      case 1:
        return indicatorConfig.higherT;

      case 2:
        return indicatorConfig.shorterT;

      case 3:
        return indicatorConfig.multiT;

      default:
        return undefined;
    }
  }, [density, indicatorConfig]);

  useEffect(() => {
    if (
      !bot ||
      !hasTimeFrameDifference ||
      (!!temporaryCandles && !shouldFetchNewCandles) ||
      !higherTCandles?.length
    ) {
      return;
    }

    const fetch = async () => {
      setIsLoading(true);
      setShouldFetchNewCandles(false);

      const waveStartTimstamp = calculateStartOfWaveTimestamp(
        transaction.transactionTimestamp,
        higherTCandles || [],
        transaction.orderType,
      );

      const startTimestamp =
        density !== 3 ? higherTCandles?.[0].startTimestamp : waveStartTimstamp;
      const endTimestamp =
        density !== 3
          ? transaction.candles?.higher[transaction.candles.higher.length - 1]
              .endTimestamp
          : [...clusteredTransactions].sort(
              (a, b) => b.transactionTimestamp - a.transactionTimestamp,
            )[0].transactionTimestamp +
            5 *
              getTimeFrameIntervalTimestamp(
                transaction.timeFrames?.multiOrder || timeFrames?.multiOrder,
              );

      /** Fetch candles */
      const { data } = await axios({
        url: ServicePaths.CANDLE_FETCHER.FetchCandles,
        params: {
          baseAsset: transaction.baseAsset,
          quoteAsset: transaction.quoteAsset,
          timeFrame,
          startTimestamp: startTimestamp || new Date().valueOf(),
          endTimestamp: endTimestamp || new Date().valueOf(),
          includeLatestCandle: true,
        },
      });

      if (density === 1) {
        setTransactionHigherTCandles(data.heikin);
      }
      setTemporaryCandles(data.heikin);
      setIsLoading(false);
    };

    fetch();
  }, [
    bot,
    clusteredTransactions,
    density,
    hasTimeFrameDifference,
    higherTCandles,
    shouldFetchNewCandles,
    temporaryCandles,
    timeFrame,
    timeFrames?.multiOrder,
    transaction,
    transaction.baseAsset,
    transaction.candles?.higher,
    transaction.orderType,
    transaction.quoteAsset,
    transaction.timeFrames?.multiOrder,
    transaction.transactionTimestamp,
  ]);

  const candles = useMemo(
    () =>
      (hasTimeFrameDifference ? temporaryCandles : transactionCandles) ?? [],
    [hasTimeFrameDifference, temporaryCandles, transactionCandles],
  );

  const handleLower = useCallback(() => {
    if (hasTimeFrameDifference) {
      setShouldFetchNewCandles(true);
    }

    setRealWakeUpTimes(undefined);
    setDensity((prev) => Math.max(1, prev - 1));
  }, [hasTimeFrameDifference, setDensity]);

  const handleRaise = useCallback(() => {
    if (hasTimeFrameDifference) {
      setShouldFetchNewCandles(true);
    }

    setRealWakeUpTimes(undefined);
    setDensity((prev) => Math.min(3, prev + 1));
  }, [hasTimeFrameDifference, setDensity]);

  const toggleEnableWakeupTimeColor = useCallback(() => {
    setEnableWakeupTimeColor((prev) => !prev);
  }, []);

  useEffect(() => {
    /** If we got no cbrt line values,
     * we'll have to calculate them by calling profit calcs
     * on a very small amount of candles. */

    /** Gather all necessary information to do the profit calcs call
     * Do the call
     * Save the cbrt range states
     */

    if (!bot) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (!!cbrtRangeStates) {
      return;
    }

    if (!higherTCandles?.length) {
      return;
    }

    if (!transaction.timeFrames || !transaction.complexParams) {
      return;
    }

    if (hasTimeFrameDifference && !transactionHigherTCandles?.length) {
      return;
    }

    setIsLoading(true);

    const params = {
      baseAsset: transaction.baseAsset,
      quoteAsset: transaction.quoteAsset,
      higherTimeFrame: transaction.timeFrames.higher,
      shorterTimeFrame: transaction.timeFrames.shorter,
      multiOrderTimeFrame: transaction.timeFrames.multiOrder,
      cbrt: transaction.complexParams.cbrt
        ? formatParam(transaction.complexParams.cbrt.toString())
        : null,
      shorterTimeFrameInterval: formatParam(
        transaction.complexParams.shorterTimeFrameInterval.toString(),
      ),
      supportCbrtRange: transaction.complexParams.supportCbrtRange,
      supportMrcState: transaction.complexParams.supportMrcState,
      latestOrder: transaction.orderType === 'BUY' ? 'SELL' : 'BUY',
      exchange: transaction.exchange,
      startTimestamp: new Date(higherTCandles[0].startTimestamp).valueOf(),
      endTimestamp: new Date(
        higherTCandles[higherTCandles.length - 1].startTimestamp,
      ).valueOf(),
    };

    const fetch = async () => {
      const result: { data: SingleProfitCalculationApiResponse | undefined } =
        await axios({
          url: ServicePaths.PROFIT_CALCULATIONS.GetSingleProfitCalculation,
          method: 'POST',
          data: params,
        });

      setCbrtRangeStates(result.data?.cbrtRangeStates);
      setMrcStates(result.data?.mrcStates);
      setProfitCalcsWakeUpTimes(result.data?.wakeupTimes);
      result.data?.transactions &&
        bot &&
        setProfitCalcsTransactions(result.data.transactions);
      setIsLoading(false);
    };

    fetch();
  }, [
    bot,
    cbrtRangeStates,
    hasTimeFrameDifference,
    higherTCandles,
    isLoading,
    transaction.baseAsset,
    transaction.complexParams,
    transaction.exchange,
    transaction.orderType,
    transaction.quoteAsset,
    transaction.timeFrames,
    transactionHigherTCandles?.length,
  ]);

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight - 400,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  useEffect(() => {
    if (!bot) {
      return;
    }

    if (realWakeUpTimes || density === 1) {
      return;
    }

    if (
      !transaction.timeFrames ||
      !transaction.complexParams ||
      !candles.length
    ) {
      return;
    }

    const fetch = async () => {
      setIsLoading(false);

      const multiOrdersRange = clusteredTransactions.map(
        (t) => t.transactionTimestamp,
      );

      const params = {
        botId: transaction.botId,
        timeFrame,
        startTimestamp: candles[0].startTimestamp,
        endTimestamp:
          candles[candles.length - 1].endTimestamp || new Date().valueOf(),
        timeFrames: JSON.stringify(transaction.timeFrames),
        shorterTimeFrameInterval:
          transaction.complexParams.shorterTimeFrameInterval,
        mrcStates: JSON.stringify(mrcStates),
        multiOrders: JSON.stringify(multiOrdersRange),
      };

      // const result: { data: number[] | undefined } = await axios({
      //   url: ServicePaths.DATA.GetClientBotWakeups,
      //   method: 'POST',
      //   data: params,
      // });

      // setRealWakeUpTimes(result?.data);
      // setIsLoading(false);
    };

    fetch();
  }, [
    bot,
    candles,
    clusteredTransactions,
    density,
    mrcStates,
    realWakeUpTimes,
    timeFrame,
    transaction.botId,
    transaction.complexParams,
    transaction.timeFrames,
  ]);

  return (
    <div className="transaction-item-candle-view-root">
      <div
        className="transaction-item-candle-view-back"
        onClick={handleCloseChartView}
      >
        <BiExitFullscreen color={colors.gray} size={24} />
      </div>

      <ChartDensityOptions
        density={density}
        handleLower={handleLower}
        handleRaise={handleRaise}
        candleInfo={candleInfo}
        isCompare={isCompare}
        setIsCompare={setIsCompare}
        showCompare={!!bot}
        enableWakeupTimeColor={enableWakeupTimeColor}
        toggleEnableWakeupTimeColor={toggleEnableWakeupTimeColor}
        useParentIndicators={useParentIndicators}
        setUseParentIndicators={setUseParentIndicators}
      />

      <div className="transaction-item-candle-view-root" ref={rootRef}>
        {!!rootDimensions && (
          // <IndicatorWrapper
          //   candles={candles}
          //   shouldFetch
          //   timeFrame={timeFrame}
          //   dimensions={rootDimensions}
          //   baseAsset={transaction.baseAsset}
          //   quoteAsset={transaction.quoteAsset}
          //   useTimeFrameSpecificConfig={false}
          //   useParentIndicators={useParentIndicators}
          //   exchange={transaction.exchange}
          //   indicatorConfig={indicatorConfigByDensity}
          // >
          <MultiChart dimensions={rootDimensions}>
            <Chart
              bot={bot}
              density={density}
              mrcStates={mrcStates}
              isCompact={isCompact}
              timeFrame={timeFrame}
              isCompare={isCompare}
              transaction={transaction}
              higherTCandles={higherTCandles}
              cbrtRangeStates={cbrtRangeStates}
              realWakeUpTimes={
                enableWakeupTimeColor ? realWakeUpTimes : undefined
              }
              temporaryCandles={temporaryCandles}
              transactionCandles={transactionCandles}
              compactTransactions={compactTransactions}
              clusteredTransactions={clusteredTransactions}
              profitCalcsWakeUpTimes={profitCalcsWakeUpTimes}
              hasTimeFrameDifference={hasTimeFrameDifference}
              profitCalcsTransactions={profitCalcsTransactions}
              setCandleInfo={setCandleInfo}
            />
          </MultiChart>
          // </IndicatorWrapper>
        )}
      </div>

      {isLoading && (
        <div className="candle-view-loading-container">
          <LoadingIcons.Grid width={25} style={{ paddingTop: 5 }} />
        </div>
      )}
    </div>
  );
};

export default CandleView;
